// FieldIQ — Threads tab — team message threads

import { useState, useEffect, useRef, useCallback } from "react";
import {
  Loader2,
  Send,
  ChevronLeft,
  Plus,
  Search,
  MessageCircle,
  RefreshCw,
  Check,
  X,
} from "lucide-react";
import { FIELDIQ_API_URL, getToken } from "../lib/api";

export function ThreadListItem({ thread, onOpen }) {
  const hasUnread = thread.unread_count > 0;
  return (
    <button
      onClick={() => onOpen(thread)}
      className={`w-full text-left bg-slate-800 border rounded-xl px-4 py-3 transition-colors hover:border-slate-600 ${
        hasUnread ? "border-cyan-700" : "border-slate-700"
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            {hasUnread && (
              <span className="w-2 h-2 rounded-full bg-cyan-400 flex-shrink-0" />
            )}
            <p className="text-sm font-medium text-white truncate">
              {thread.title}
            </p>
            {thread.status === "archived" && (
              <span className="text-xs text-slate-500 flex-shrink-0">
                archived
              </span>
            )}
          </div>
          {thread.last_message ? (
            <p className="text-xs text-slate-400 truncate">
              {thread.last_sender
                ? `${thread.last_sender.split(" ")[0]}: `
                : ""}
              {thread.last_message}
            </p>
          ) : (
            <p className="text-xs text-slate-600 italic">No messages yet</p>
          )}
        </div>
        <div className="flex flex-col items-end gap-1 flex-shrink-0">
          {thread.last_message_at && (
            <span className="text-xs text-slate-500">
              {new Date(thread.last_message_at).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
              })}
            </span>
          )}
          {hasUnread && (
            <span className="px-1.5 py-0.5 text-xs bg-cyan-600 text-white rounded-full font-medium">
              {thread.unread_count}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

export function ThreadDetail({ thread, user, onBack }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const bottomRef = useRef(null);
  const isArchived = thread.status !== "active";

  const loadMessages = useCallback(async () => {
    try {
      const data = await fetch(
        `${FIELDIQ_API_URL}/threads/${thread.id}/messages`,
        { headers: { Authorization: `Bearer ${getToken()}` } },
      ).then((r) => r.json());
      if (Array.isArray(data)) setMessages(data);
    } catch {}
    setLoading(false);
  }, [thread.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadMessages();
    // Mark as read
    fetch(`${FIELDIQ_API_URL}/threads/${thread.id}/read`, {
      method: "POST",
      headers: { Authorization: `Bearer ${getToken()}` },
    }).catch(() => {});
  }, [loadMessages, thread.id]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    const optimistic = {
      id: `tmp-${Date.now()}`,
      sender_id: user?.id,
      sender_name:
        user?.name ||
        `${user?.first_name || ""} ${user?.last_name || ""}`.trim(),
      body: text.trim(),
      created_at: new Date().toISOString(),
      is_deleted: false,
    };
    setMessages((prev) => [...prev, optimistic]);
    setText("");
    try {
      const res = await fetch(
        `${FIELDIQ_API_URL}/threads/${thread.id}/messages`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`,
          },
          body: JSON.stringify({ body: optimistic.body }),
        },
      );
      if (res.ok) {
        const { id, created_at } = await res.json();
        setMessages((prev) =>
          prev.map((m) =>
            m.id === optimistic.id ? { ...m, id, created_at } : m,
          ),
        );
      } else {
        setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
      }
    } catch {
      setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
    }
    setSending(false);
  };

  const handleEdit = async (msgId) => {
    if (!editText.trim()) return;
    try {
      await fetch(`${FIELDIQ_API_URL}/threads/${thread.id}/messages/${msgId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({ body: editText.trim() }),
      });
      setMessages((prev) =>
        prev.map((m) =>
          m.id === msgId
            ? {
                ...m,
                body: editText.trim(),
                edited_at: new Date().toISOString(),
              }
            : m,
        ),
      );
    } catch {}
    setEditingId(null);
    setEditText("");
  };

  const handleDelete = async (msgId) => {
    if (!window.confirm("Delete this message?")) return;
    try {
      await fetch(`${FIELDIQ_API_URL}/threads/${thread.id}/messages/${msgId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setMessages((prev) =>
        prev.map((m) =>
          m.id === msgId
            ? { ...m, is_deleted: true, body: "This message was deleted." }
            : m,
        ),
      );
    } catch {}
  };

  const myId = user?.id || user?.sub;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-3 px-1 py-2 border-b border-slate-700 flex-shrink-0">
        <button
          onClick={onBack}
          className="p-1.5 text-slate-400 hover:text-white"
        >
          <ChevronLeft size={18} />
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-white truncate">
            {thread.title}
          </p>
          <p className="text-xs text-slate-500">
            {thread.member_count || "?"} members
            {isArchived && " · archived"}
          </p>
        </div>
      </div>

      {/* Moderation notice */}
      <div className="px-3 py-1.5 bg-slate-800/60 border-b border-slate-700 flex-shrink-0">
        <p className="text-xs text-slate-500 text-center">
          LD leadership has read access to all threads for moderation.
        </p>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2">
        {loading ? (
          <div className="flex justify-center pt-8">
            <Loader2 size={20} className="animate-spin text-slate-500" />
          </div>
        ) : messages.length === 0 ? (
          <p className="text-center text-xs text-slate-600 pt-8">
            No messages yet. Say hello!
          </p>
        ) : (
          messages.map((msg) => {
            const isMine = msg.sender_id === myId;
            const age =
              (Date.now() - new Date(msg.created_at).getTime()) / 1000;
            const canEdit = isMine && !msg.is_deleted && age < 300;

            return (
              <div
                key={msg.id}
                className={`flex gap-2 ${isMine ? "flex-row-reverse" : ""}`}
              >
                <div
                  className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-auto ${
                    isMine ? "bg-cyan-600" : "bg-slate-600"
                  }`}
                >
                  {(msg.sender_name || "?")[0].toUpperCase()}
                </div>
                <div
                  className={`max-w-[75%] ${isMine ? "items-end" : "items-start"} flex flex-col`}
                >
                  {!isMine && (
                    <span className="text-xs text-slate-500 mb-0.5 px-1">
                      {msg.sender_name}
                    </span>
                  )}
                  {editingId === msg.id ? (
                    <div className="space-y-1 w-full">
                      <textarea
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        rows={2}
                        className="w-full px-3 py-2 bg-slate-700 border border-slate-500 rounded-xl text-sm text-white resize-none"
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(msg.id)}
                          className="text-xs text-cyan-400 hover:text-cyan-300"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setEditingId(null)}
                          className="text-xs text-slate-500"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div
                      className={`px-3 py-2 rounded-2xl text-sm leading-snug ${
                        msg.is_deleted
                          ? "bg-slate-700/50 text-slate-500 italic"
                          : isMine
                            ? "bg-cyan-700 text-white"
                            : "bg-slate-700 text-slate-100"
                      }`}
                    >
                      {msg.body}
                      {msg.edited_at && !msg.is_deleted && (
                        <span className="text-xs opacity-50 ml-1">
                          (edited)
                        </span>
                      )}
                    </div>
                  )}
                  <div className="flex items-center gap-2 px-1 mt-0.5">
                    <span className="text-xs text-slate-600">
                      {new Date(msg.created_at).toLocaleTimeString("en-US", {
                        hour: "numeric",
                        minute: "2-digit",
                      })}
                    </span>
                    {canEdit && editingId !== msg.id && (
                      <>
                        <button
                          onClick={() => {
                            setEditingId(msg.id);
                            setEditText(msg.body);
                          }}
                          className="text-xs text-slate-600 hover:text-slate-400"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(msg.id)}
                          className="text-xs text-slate-600 hover:text-rose-400"
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={bottomRef} />
      </div>

      {/* Compose */}
      {isArchived ? (
        <div className="px-4 py-3 border-t border-slate-700 text-center text-xs text-slate-500 flex-shrink-0">
          This thread is archived — read only.
        </div>
      ) : (
        <div className="flex items-end gap-2 px-3 py-3 border-t border-slate-700 flex-shrink-0">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            placeholder="Message..."
            rows={1}
            className="flex-1 bg-slate-700 border border-slate-600 rounded-2xl px-4 py-2.5 text-sm text-white placeholder-slate-500 resize-none focus:outline-none focus:border-cyan-600"
            style={{ maxHeight: 120, overflowY: "auto" }}
          />
          <button
            onClick={handleSend}
            disabled={!text.trim() || sending}
            className="w-10 h-10 rounded-full bg-cyan-600 hover:bg-cyan-700 disabled:opacity-40 flex items-center justify-center flex-shrink-0"
          >
            {sending ? (
              <Loader2 size={16} className="animate-spin text-white" />
            ) : (
              <Send size={16} className="text-white" />
            )}
          </button>
        </div>
      )}
    </div>
  );
}

export const ThreadsTab = ({ user, facility, isOnline }) => {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeThread, setActiveThread] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newMemberIds, setNewMemberIds] = useState(new Set());
  const [facUsers, setFacUsers] = useState([]);
  const [loadingFac, setLoadingFac] = useState(false);
  const [memberSearch, setMemberSearch] = useState("");
  const [creating, setCreating] = useState(false);

  const ldId = facility?.facility_id || "";
  const myId = user?.id || user?.sub || "";

  const fetchThreads = useCallback(async () => {
    if (!ldId) return;
    setLoading(true);
    try {
      const status = showArchived ? "archived" : "active";
      const data = await fetch(
        `${FIELDIQ_API_URL}/threads?ld_id=${encodeURIComponent(ldId)}&status=${status}`,
        { headers: { Authorization: `Bearer ${getToken()}` } },
      ).then((r) => r.json());
      if (Array.isArray(data)) setThreads(data);
    } catch {}
    setLoading(false);
  }, [ldId, showArchived]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchThreads();
  }, [fetchThreads]);

  const openNew = async () => {
    setNewTitle("");
    setNewMemberIds(new Set());
    setMemberSearch("");
    setShowNew(true);
    if (facUsers.length === 0 && ldId) {
      setLoadingFac(true);
      try {
        const data = await fetch(
          `${FIELDIQ_API_URL}/threads/members?ld_id=${encodeURIComponent(ldId)}`,
          { headers: { Authorization: `Bearer ${getToken()}` } },
        ).then((r) => r.json());
        if (Array.isArray(data))
          setFacUsers(data.filter((u) => u.user_id !== myId));
      } catch {}
      setLoadingFac(false);
    }
  };

  const toggleMember = (uid) =>
    setNewMemberIds((prev) => {
      const next = new Set(prev);
      next.has(uid) ? next.delete(uid) : next.add(uid);
      return next;
    });

  const handleCreate = async () => {
    if (!newTitle.trim() || creating) return;
    setCreating(true);
    try {
      const res = await fetch(`${FIELDIQ_API_URL}/threads`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({
          ld_id: ldId,
          title: newTitle.trim(),
          thread_type: "adhoc",
          member_ids: [...newMemberIds],
        }),
      });
      if (res.ok) {
        const { id } = await res.json();
        setShowNew(false);
        await fetchThreads();
        setActiveThread(
          (prev) =>
            prev || {
              id,
              title: newTitle.trim(),
              status: "active",
              member_count: newMemberIds.size + 1,
            },
        );
      }
    } catch {}
    setCreating(false);
  };

  const filteredUsers = facUsers.filter((u) =>
    (u.full_name || u.email || "")
      .toLowerCase()
      .includes(memberSearch.toLowerCase()),
  );

  if (activeThread) {
    return (
      <ThreadDetail
        thread={activeThread}
        user={user}
        onBack={() => {
          setActiveThread(null);
          fetchThreads();
        }}
      />
    );
  }

  return (
    <div className="space-y-4 h-full">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-white">Team Threads</h2>
          <p className="text-xs text-slate-400">
            Walk groups and committee chats
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={openNew}
            disabled={!isOnline}
            className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-cyan-600 hover:bg-cyan-700 text-white transition-colors disabled:opacity-40"
          >
            <Plus size={12} /> New
          </button>
          <button
            onClick={() => setShowArchived((v) => !v)}
            className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
              showArchived
                ? "bg-slate-700 border-slate-600 text-slate-300"
                : "border-slate-700 text-slate-500 hover:text-slate-300"
            }`}
          >
            {showArchived ? "Active" : "Archived"}
          </button>
          <button
            onClick={fetchThreads}
            disabled={loading || !isOnline}
            className="p-2 text-slate-500 hover:text-slate-300"
          >
            <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 size={22} className="animate-spin text-slate-500" />
        </div>
      ) : threads.length === 0 ? (
        <div className="text-center py-14 border-2 border-dashed border-slate-700 rounded-xl">
          <MessageCircle size={28} className="mx-auto mb-3 text-slate-600" />
          <p className="text-sm text-slate-400">
            {showArchived ? "No archived threads." : "No active threads yet."}
          </p>
          {!showArchived && (
            <button
              onClick={openNew}
              disabled={!isOnline}
              className="mt-3 text-xs text-cyan-400 hover:text-cyan-300 disabled:opacity-40"
            >
              Start a new thread
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {threads.map((t) => (
            <ThreadListItem key={t.id} thread={t} onOpen={setActiveThread} />
          ))}
        </div>
      )}

      {/* New thread sheet */}
      {showNew && (
        <div className="fixed inset-0 z-50 flex flex-col bg-slate-900">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700">
            <h3 className="text-sm font-semibold text-white">New Thread</h3>
            <button
              onClick={() => setShowNew(false)}
              className="p-1.5 text-slate-400 hover:text-white"
            >
              <X size={18} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">
                Thread name
              </label>
              <input
                type="text"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder="e.g. Precinct 12 coordination"
                className="w-full bg-slate-800 border border-slate-600 rounded-xl px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-cyan-600"
                autoFocus
              />
            </div>

            <div>
              <label className="text-xs text-slate-400 mb-1 block">
                Add members
                {newMemberIds.size > 0
                  ? ` (${newMemberIds.size} selected)`
                  : ""}
              </label>
              <div className="relative mb-2">
                <Search
                  size={13}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"
                />
                <input
                  type="text"
                  value={memberSearch}
                  onChange={(e) => setMemberSearch(e.target.value)}
                  placeholder="Search members..."
                  className="w-full bg-slate-800 border border-slate-700 rounded-xl pl-8 pr-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-cyan-600"
                />
              </div>

              {loadingFac ? (
                <div className="flex justify-center py-6">
                  <Loader2 size={18} className="animate-spin text-slate-500" />
                </div>
              ) : filteredUsers.length === 0 ? (
                <p className="text-xs text-slate-500 text-center py-4">
                  {memberSearch
                    ? "No matches."
                    : "No other members in this LD."}
                </p>
              ) : (
                <div className="space-y-1 max-h-64 overflow-y-auto">
                  {filteredUsers.map((u) => {
                    const selected = newMemberIds.has(u.user_id);
                    return (
                      <button
                        key={u.user_id}
                        onClick={() => toggleMember(u.user_id)}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors text-left ${
                          selected
                            ? "bg-cyan-900/40 border border-cyan-700"
                            : "bg-slate-800 border border-transparent hover:bg-slate-700"
                        }`}
                      >
                        <div className="w-7 h-7 rounded-full bg-slate-600 flex items-center justify-center text-xs font-bold text-white flex-shrink-0">
                          {(u.full_name || u.email || "?")[0].toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-white truncate">
                            {u.full_name || u.email}
                          </p>
                          {u.full_name && (
                            <p className="text-xs text-slate-500 truncate">
                              {u.email}
                            </p>
                          )}
                        </div>
                        {selected && (
                          <Check
                            size={14}
                            className="text-cyan-400 flex-shrink-0"
                          />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div className="px-4 py-4 border-t border-slate-700">
            <button
              onClick={handleCreate}
              disabled={!newTitle.trim() || creating}
              className="w-full py-3 rounded-xl bg-cyan-600 hover:bg-cyan-700 disabled:opacity-40 text-sm font-semibold text-white transition-colors flex items-center justify-center gap-2"
            >
              {creating ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <MessageCircle size={16} />
              )}
              {creating ? "Creating..." : "Create Thread"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// GEO LOOKUP — IndexedDB helpers
